/**
 * Remote configuration shape and compiled-in defaults (fail-safe when D1/KV are unavailable).
 * Keys map 1:1 to rows in D1 `app_config`.
 */
export type VipTier = 'free' | 'pro';

export interface RateLimitBuckets {
  read: number;
  write: number;
}

export interface MediaLimits {
  /** Max bytes per image upload. */
  image_max_bytes: number;
  /** Max bytes per audio upload (purpose=asset). */
  audio_max_bytes: number;
  allowed_image_types: string[];
  allowed_audio_types: string[];
}

export type ContentProfile = 'sfw' | 'nsfw' | 'any';

/** One row of the official model registry (config key `models`). `route` is the AI Gateway model id. */
export interface ModelEntry {
  id: string;
  /** Family / vendor for display / grouping (qwen, glm, llama, gemma, deepseek, google-ai-studio, openai, …). Not the host. */
  provider: string;
  /** AI Gateway model id: `{provider}/{model}` or `@cf/{org}/{model}` (Workers AI hosted). */
  route: string;
  display_name: string;
  context_length: number;
  max_output: number;
  /** USD per 1M tokens (settlement audit + `cost_estimate`). */
  pricing: { input: number; output: number; cached_input?: number };
  /** Explicit credits ("钻石") per 1K total tokens; `null` / absent = derived from `pricing` via `creditsPer1k()` (change 11). Unused for settle when `charge_mode=fixed`. */
  credits_per_1k?: number | null;
  /** Explicit credits per official turn; `null` / absent = `ceil(creditsPer1k())` (switch-credits-to-fixed-turn). */
  credits_per_turn?: number | null;
  capabilities: { reasoning?: boolean; tools?: boolean; vision?: boolean };
  content_profile: ContentProfile;
  channel: 'official';
  tier_min: VipTier;
  priority: number;
  enabled: boolean;
}

export interface AiLimits {
  first_byte_ms: number;
  total_ms: number;
  byok_max_secrets: number;
  max_tokens_ceiling: Record<VipTier, number>;
  /** Inclusive [min, max] clamps applied at execution point 2 for official channels. */
  sampling_ranges: Record<string, [number, number]>;
  /** Group chats: max member turns per `generate` on the official channel (clamp `policy.group_max_turns`); BYOK / local uncapped. */
  group_max_turns: Record<VipTier, number>;
  /** Tool-calling loop: tools are offered while `depth < max_tool_depth[tier]` (ST `ToolManager.RECURSE_LIMIT = 5`). */
  max_tool_depth: Record<VipTier, number>;
  /** auto-continue: max `continue` turns appended per generate (ST has no cap; clamp `policy.auto_continue_max`). */
  auto_continue_max: Record<VipTier, number>;
  /** AI character generation (change 13): output caps, per-detail-level estimates (for the pre-charge hint) and JSON repair retries. */
  character_gen?: CharacterGenLimits;
  /**
   * add-mvp-protocol-gaps-3: per-user cap on simultaneously active generations (compiled / streaming / awaiting_tools
   * across all chats). `null` or absent = unlimited (the default); set values yield 429 CHAT_CONCURRENCY_LIMIT.
   */
  max_concurrent_generations?: Record<VipTier, number | null>;
}

export type CharacterGenDetailLevel = 'brief' | 'balanced' | 'detailed';

export interface CharacterGenLimits {
  /** Hard cap on output tokens for a single character-gen call (before the per-tier ceiling). */
  max_tokens: number;
  /** Expected output tokens per detail level; drives `message_start.estimate_tokens` and the reservation size. */
  estimate_tokens: Record<CharacterGenDetailLevel, number>;
  /** How many times an unparsable / schema-invalid JSON answer is sent back for repair (0 = never). */
  repair_retries: number;
}

export const DEFAULT_CHARACTER_GEN_LIMITS: CharacterGenLimits = {
  // thinking models (Gemini 2.5) spend `max_tokens` on reasoning first: the cap needs headroom above the estimate
  max_tokens: 8192,
  estimate_tokens: { brief: 600, balanced: 1200, detailed: 2000 },
  repair_retries: 1,
};

/** `ai_limits.character_gen` with per-field fallback for KV documents written before migration 0011. */
export function characterGenLimitsFor(limits: Partial<Pick<AiLimits, 'character_gen'>> | undefined): CharacterGenLimits {
  const raw = limits?.character_gen;
  const d = DEFAULT_CHARACTER_GEN_LIMITS;
  const est = raw?.estimate_tokens;
  const pick = (k: CharacterGenDetailLevel) => (est && Number.isFinite(est[k]) && (est[k] as number) > 0 ? Math.floor(est[k] as number) : d.estimate_tokens[k]);
  return {
    max_tokens: raw && Number.isFinite(raw.max_tokens) && raw.max_tokens > 0 ? Math.floor(raw.max_tokens) : d.max_tokens,
    estimate_tokens: { brief: pick('brief'), balanced: pick('balanced'), detailed: pick('detailed') },
    repair_retries: raw && Number.isInteger(raw.repair_retries) && raw.repair_retries >= 0 ? Math.min(raw.repair_retries, 2) : d.repair_retries,
  };
}

export const DEFAULT_GROUP_MAX_TURNS: Record<VipTier, number> = { free: 2, pro: 5 };
export const DEFAULT_MAX_TOOL_DEPTH: Record<VipTier, number> = { free: 3, pro: 5 };
export const DEFAULT_AUTO_CONTINUE_MAX: Record<VipTier, number> = { free: 2, pro: 5 };

function tierLimit(table: Record<VipTier, number> | undefined, fallback: Record<VipTier, number>, tier: string): number {
  const src = table ?? fallback;
  const t = (tier in src ? tier : 'free') as VipTier;
  const n = src[t] ?? fallback[t];
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback.free;
}

/** `ai_limits.group_max_turns[tier]` with the compiled-in default for KV documents written before migration 0005. */
export function groupMaxTurnsFor(limits: Partial<Pick<AiLimits, 'group_max_turns'>> | undefined, tier: string): number {
  return tierLimit(limits?.group_max_turns, DEFAULT_GROUP_MAX_TURNS, tier);
}

/** `ai_limits.max_tool_depth[tier]` with the compiled-in default for KV documents written before migration 0006. */
export function toolDepthFor(limits: Partial<Pick<AiLimits, 'max_tool_depth'>> | undefined, tier: string): number {
  return tierLimit(limits?.max_tool_depth, DEFAULT_MAX_TOOL_DEPTH, tier);
}

/** `ai_limits.auto_continue_max[tier]` with the compiled-in default for KV documents written before migration 0006. */
export function autoContinueMaxFor(limits: Partial<Pick<AiLimits, 'auto_continue_max'>> | undefined, tier: string): number {
  return tierLimit(limits?.auto_continue_max, DEFAULT_AUTO_CONTINUE_MAX, tier);
}

/** `ai_limits.max_concurrent_generations[tier]`: `null` = unlimited (the default when the key is absent or malformed). */
export function concurrentGenerationsFor(limits: Partial<Pick<AiLimits, 'max_concurrent_generations'>> | undefined, tier: string): number | null {
  const table = limits?.max_concurrent_generations;
  if (!table || typeof table !== 'object') return null;
  const t = (tier in table ? tier : 'free') as VipTier;
  const n = table[t];
  return typeof n === 'number' && Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
}

/** Plan entitlements (change 9): per-tier count caps; `null` = unlimited. Counting rules live in core `vault/entitlements.ts`. */
export type PlanLimitResource = 'characters' | 'world_books' | 'personas' | 'ai_character_gen_per_day';
export const PLAN_LIMIT_RESOURCES: PlanLimitResource[] = ['characters', 'world_books', 'personas', 'ai_character_gen_per_day'];
export type PlanLimits = Record<PlanLimitResource, Record<VipTier, number | null>>;
// Count caps stay off; AI character generation is a pro feature (`free: 0`). The daily count is only an abuse cap
// for pro (30 as of 2026-09-12; each call already charges credits). The gating machinery stays configurable.
export const DEFAULT_PLAN_LIMITS: PlanLimits = {
  characters: { free: null, pro: null },
  world_books: { free: null, pro: null },
  personas: { free: null, pro: null },
  ai_character_gen_per_day: { free: 0, pro: 30 },
};

/** Account policy (change 10): deletion grace + email OTP knobs. */
export interface AccountPolicy {
  /** Days between `POST /api/me/delete` and the physical purge. */
  deletion_grace_days: number;
  otp_ttl_seconds: number;
  otp_max_attempts: number;
  otp_resend_cooldown_seconds: number;
  /** Max codes per (email, purpose) per rolling hour. */
  otp_hourly_limit: number;
  /** Days a soft-deleted entity (chat / character / book / persona …) stays restorable before the daily purge (add-chat-retention; 7–90). */
  trash_retention_days: number;
}
export const DEFAULT_ACCOUNT_POLICY: AccountPolicy = { deletion_grace_days: 30, otp_ttl_seconds: 600, otp_max_attempts: 5, otp_resend_cooldown_seconds: 60, otp_hourly_limit: 5, trash_retention_days: 30 };
export const TRASH_RETENTION_DAYS_RANGE = { min: 7, max: 90 } as const;

/** `account_policy` with per-field fallback to the compiled-in default (documents written before migration 0008, garbage values). */
export function accountPolicyFor(policy: Partial<AccountPolicy> | undefined): AccountPolicy {
  const out = { ...DEFAULT_ACCOUNT_POLICY };
  for (const key of Object.keys(DEFAULT_ACCOUNT_POLICY) as Array<keyof AccountPolicy>) {
    const v = policy?.[key];
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) out[key] = Math.floor(v);
  }
  // `account_policy` is edited in D1 directly (not admin-writable): clamp instead of rejecting
  out.trash_retention_days = Math.min(TRASH_RETENTION_DAYS_RANGE.max, Math.max(TRASH_RETENTION_DAYS_RANGE.min, out.trash_retention_days));
  return out;
}

/** `plan_limits[resource][tier]` with the compiled-in default for KV documents written before migration 0007; `null` = unlimited. */
export function planLimitFor(limits: Partial<PlanLimits> | undefined, resource: PlanLimitResource, tier: string): number | null {
  const table = limits?.[resource] ?? DEFAULT_PLAN_LIMITS[resource];
  const t = (tier in table ? tier : 'free') as VipTier;
  const v = table[t];
  if (v === null) return null;
  if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return Math.floor(v);
  const fallback = DEFAULT_PLAN_LIMITS[resource][t];
  return fallback === null ? null : fallback;
}

/** One purchasable credits pack (change 11; payment itself lands with change 12). */
export interface CreditPack {
  id: string;
  usd: number;
  credits: number;
  bonus_pct: number;
  tag?: string;
  /** Store consumable product ids (change 12); absent until the products exist in App Store Connect / Play Console. */
  store_product_ids?: { ios?: string; android?: string; paypal?: string };
}

/** Credits ("钻石") policy (change 11): pricing conversion, daily reward, packs. */
export interface CreditsPolicy {
  /** Credits per USD of registry price (`$1 = 1000`). */
  credits_per_usd: number;
  /** Multiplier on the USD price (1.0 = product copy "平台不加价"). */
  margin: number;
  /** Assumed share of output tokens when blending input / output prices into one per-1K rate. */
  blend_output_share: number;
  /** Minimum credits charged per completed official turn. */
  min_charge: number;
  /**
   * `fixed` = one integer price per official turn (`credits_per_turn`).
   * `token` = legacy `ceil(total_tokens × credits_per_1k / 1000)` (kept, not the default).
   */
  charge_mode: 'fixed' | 'token';
  daily_reward: Record<VipTier, number>;
  /** Credits granted once when the wallet is created (200 = p1 pack, as of 2026-09-12). */
  signup_bonus: number;
  /** Reserved for a future escalating streak bonus; `null` = flat daily reward. */
  streak_bonus: null | Record<string, number>;
  /** Reservations older than this are ignored / dropped (covers the longest generation + heartbeat). */
  reservation_ttl_seconds: number;
  /** Default window of the transactions list. */
  ledger_window_days: number;
  packs: CreditPack[];
}

export const DEFAULT_CREDITS_POLICY: CreditsPolicy = {
  credits_per_usd: 1000,
  margin: 1.0,
  blend_output_share: 0.3,
  min_charge: 1,
  charge_mode: 'fixed',
  daily_reward: { free: 10, pro: 30 },
  signup_bonus: 200,
  streak_bonus: null,
  reservation_ttl_seconds: 900,
  ledger_window_days: 30,
  // 2026-09-12: sizes follow the store products created in App Store Connect (`app.pockettavern.credits.<credits>`);
  // 2026-09-14: Play one-time products use the same ids (migration 0017).
  // Web PayPal orders charge `usd` (USD). iOS / Android charge the store's localized price.
  packs: [
    { id: 'p1', usd: 0.99, credits: 200, bonus_pct: 0, store_product_ids: { ios: 'app.pockettavern.credits.200', android: 'app.pockettavern.credits.200' } },
    { id: 'p2', usd: 4.99, credits: 1000, bonus_pct: 0, store_product_ids: { ios: 'app.pockettavern.credits.1000', android: 'app.pockettavern.credits.1000' } },
    { id: 'p3', usd: 19.99, credits: 5000, bonus_pct: 11, tag: 'popular', store_product_ids: { ios: 'app.pockettavern.credits.5000', android: 'app.pockettavern.credits.5000' } },
    { id: 'p4', usd: 69.99, credits: 20000, bonus_pct: 25, tag: 'best', store_product_ids: { ios: 'app.pockettavern.credits.20000', android: 'app.pockettavern.credits.20000' } },
  ],
};

const isPosNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;
const isNonNegNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0;

/** `credits_policy` with per-field fallback to the compiled-in default (documents written before migration 0009, garbage values). */
export function creditsPolicyFor(policy: Partial<CreditsPolicy> | undefined): CreditsPolicy {
  const d = DEFAULT_CREDITS_POLICY;
  const reward = policy?.daily_reward;
  const packs = Array.isArray(policy?.packs)
    ? policy.packs.filter((p): p is CreditPack => !!p && typeof p.id === 'string' && isPosNumber(p.usd) && isPosNumber(p.credits) && Number.isInteger(p.credits) && isNonNegNumber(p.bonus_pct))
    : d.packs;
  return {
    credits_per_usd: isPosNumber(policy?.credits_per_usd) ? policy.credits_per_usd : d.credits_per_usd,
    margin: isPosNumber(policy?.margin) ? policy.margin : d.margin,
    blend_output_share: isNonNegNumber(policy?.blend_output_share) && policy.blend_output_share <= 1 ? policy.blend_output_share : d.blend_output_share,
    min_charge: isNonNegNumber(policy?.min_charge) ? Math.floor(policy.min_charge) : d.min_charge,
    charge_mode: policy?.charge_mode === 'token' ? 'token' : 'fixed',
    daily_reward: {
      free: isNonNegNumber(reward?.free) ? Math.floor(reward.free) : d.daily_reward.free,
      pro: isNonNegNumber(reward?.pro) ? Math.floor(reward.pro) : d.daily_reward.pro,
    },
    signup_bonus: isNonNegNumber(policy?.signup_bonus) ? Math.floor(policy.signup_bonus) : d.signup_bonus,
    streak_bonus: policy?.streak_bonus && typeof policy.streak_bonus === 'object' ? policy.streak_bonus : null,
    reservation_ttl_seconds: isPosNumber(policy?.reservation_ttl_seconds) ? Math.floor(policy.reservation_ttl_seconds) : d.reservation_ttl_seconds,
    ledger_window_days: isPosNumber(policy?.ledger_window_days) ? Math.floor(policy.ledger_window_days) : d.ledger_window_days,
    packs: packs.length > 0 ? packs : d.packs,
  };
}

/** Live diamond rate: always a positive integer (ceil; never a fraction). */
function ceilCredits(n: number): number {
  const x = Number.isFinite(n) ? n : 0;
  return Math.max(1, Math.ceil(Math.round(x * 1e6) / 1e6));
}

/**
 * Single displayed + settled rate in credits per 1K total tokens (roadmap D12, 2026-09-22): the explicit
 * registry override wins; otherwise blend the USD input / output prices, convert to credits, apply the margin and
 * round UP to an integer so live prices never show a decimal.
 */
export function creditsPer1k(model: Pick<ModelEntry, 'pricing' | 'credits_per_1k'>, policy: CreditsPolicy): number {
  if (isPosNumber(model.credits_per_1k)) return ceilCredits(model.credits_per_1k);
  const s = policy.blend_output_share;
  const usdPer1k = ((model.pricing.input * (1 - s) + model.pricing.output * s) / 1_000_000) * 1000;
  const raw = usdPer1k * policy.credits_per_usd * policy.margin;
  return ceilCredits(raw);
}

/** Credits charged for one completed official turn: `max(min_charge, ceil(total_tokens × rate / 1000))`. */
export function chargeFor(totalTokens: number, creditsPer1kRate: number, policy: Pick<CreditsPolicy, 'min_charge'>): number {
  const tokens = Number.isFinite(totalTokens) && totalTokens > 0 ? totalTokens : 0;
  const raw = (tokens * creditsPer1kRate) / 1000;
  return Math.max(policy.min_charge, Math.ceil(Math.round(raw * 1e6) / 1e6));
}

/** Credits reserved before calling the provider: the exact prompt plus the (clamped) max output at the same rate. */
export function reserveFor(promptTokens: number, maxTokens: number, creditsPer1kRate: number, policy: Pick<CreditsPolicy, 'min_charge'>): number {
  return chargeFor(Math.max(0, promptTokens) + Math.max(0, maxTokens), creditsPer1kRate, policy);
}

/** Integer credits charged for one official turn when `charge_mode=fixed`. */
export function chargeFixed(creditsPerTurnRate: number, policy: Pick<CreditsPolicy, 'min_charge'>): number {
  const n = Number.isFinite(creditsPerTurnRate) ? Math.floor(creditsPerTurnRate) : 0;
  return Math.max(policy.min_charge, n);
}

/** Displayed + settled per-turn price. Explicit override wins; otherwise ceil the 1K rate. */
export function creditsPerTurn(model: Pick<ModelEntry, 'pricing' | 'credits_per_1k' | 'credits_per_turn'>, policy: CreditsPolicy): number {
  if (isPosNumber(model.credits_per_turn)) return chargeFixed(model.credits_per_turn, policy);
  return chargeFixed(Math.ceil(creditsPer1k(model, policy)), policy);
}

export interface ChargeLiveInput {
  policy: CreditsPolicy;
  phase: 'reserve' | 'settle';
  credits_per_1k: number;
  credits_per_turn?: number;
  prompt_tokens?: number;
  max_tokens?: number;
  total_tokens?: number;
}

/**
 * Live meter: `fixed` (default) ignores tokens; `token` keeps `reserveFor` / `chargeFor`.
 * Missing `credits_per_turn` falls back to `ceil(credits_per_1k)`.
 */
export function chargeLive(input: ChargeLiveInput): number {
  if (input.policy.charge_mode === 'token') {
    return input.phase === 'reserve'
      ? reserveFor(input.prompt_tokens ?? 0, input.max_tokens ?? 0, input.credits_per_1k, input.policy)
      : chargeFor(input.total_tokens ?? 0, input.credits_per_1k, input.policy);
  }
  const turn = isPosNumber(input.credits_per_turn) ? input.credits_per_turn : Math.ceil(input.credits_per_1k);
  return chargeFixed(Number.isFinite(turn) ? turn : input.policy.min_charge, input.policy);
}

/** Subscription SKU (change 12): one tier (Pro), monthly or yearly. */
export interface SubscriptionProduct {
  id: string;
  period: 'month' | 'year';
  usd: number;
  store_product_ids: { ios?: string; android?: string; paypal?: string };
}

/** IAP catalog + lifecycle knobs (change 12). Credit packs stay in `credits_policy.packs`. */
export interface BillingProducts {
  subscriptions: SubscriptionProduct[];
  /** Store billing-retry grace (Apple up to 16 days): `in_grace` keeps Pro until it ends. */
  grace_days: number;
  /** Refunded credit packs claw the credits back (may overdraw); subscriptions are downgraded regardless. */
  clawback_credit_refunds: boolean;
}

export const DEFAULT_BILLING_PRODUCTS: BillingProducts = {
  subscriptions: [
    // Android: Play has one subscription product with two base plans → `<productId>:<basePlanId>` (migration 0017; verifier composes the same id)
    { id: 'pro_monthly', period: 'month', usd: 19.99, store_product_ids: { ios: 'app.pockettavern.pro.monthly', android: 'app.pockettavern.pro:pro-monthly' } },
    { id: 'pro_yearly', period: 'year', usd: 99.99, store_product_ids: { ios: 'app.pockettavern.pro.yearly', android: 'app.pockettavern.pro:pro-yearly' } },
  ],
  grace_days: 16,
  clawback_credit_refunds: true,
};

/** `billing_products` with per-field fallback (documents written before migration 0010, garbage values). */
export function billingProductsFor(raw: Partial<BillingProducts> | undefined): BillingProducts {
  const d = DEFAULT_BILLING_PRODUCTS;
  const subs = Array.isArray(raw?.subscriptions)
    ? raw.subscriptions.filter((s): s is SubscriptionProduct => !!s && typeof s.id === 'string' && (s.period === 'month' || s.period === 'year') && isPosNumber(s.usd) && !!s.store_product_ids && typeof s.store_product_ids === 'object')
    : d.subscriptions;
  return {
    subscriptions: subs.length > 0 ? subs : d.subscriptions,
    grace_days: isNonNegNumber(raw?.grace_days) ? Math.floor(raw.grace_days) : d.grace_days,
    clawback_credit_refunds: typeof raw?.clawback_credit_refunds === 'boolean' ? raw.clawback_credit_refunds : d.clawback_credit_refunds,
  };
}

/** Memory modules (change 15): embedding model, chunking, Data Bank quotas, summarize output cap. */
export interface MemoryPolicy {
  /** Workers AI embedding model id (bge-m3: 1024 dims, multilingual). Changing it requires a new Vectorize index + reindex. */
  embedding_model: string;
  embedding_dimensions: number;
  /** Default chunk size (characters) for chat messages when `extension_settings.vectors.message_chunk_size` is absent. */
  chunk_size: number;
  /** Default overlap between neighbouring chunks (percent of chunk size). */
  chunk_overlap_pct: number;
  /** Hard cap on `memory_chunks` rows per user; beyond it indexing pauses (`memory_quota_exceeded`). */
  max_chunks_per_user: number;
  /** Max bytes of one Data Bank file (text / markdown). */
  databank_file_max_bytes: number;
  /** Total Data Bank bytes per tier. */
  databank_total_bytes: Record<VipTier, number>;
  /** Output cap for one summarize call (`overrideResponseLength > 0` in ST settings takes the smaller). */
  summarize_max_tokens: number;
  /** Default number of recent messages used as the retrieval query when `extension_settings.vectors.query` is absent. */
  query_messages: number;
}

export const DEFAULT_MEMORY_POLICY: MemoryPolicy = {
  embedding_model: '@cf/baai/bge-m3',
  embedding_dimensions: 1024,
  chunk_size: 400,
  chunk_overlap_pct: 10,
  max_chunks_per_user: 100_000,
  databank_file_max_bytes: 1024 * 1024, // DO SQLite caps one row at 2 MB and the text is stored inline
  databank_total_bytes: { free: 10 * 1024 * 1024, pro: 100 * 1024 * 1024 },
  summarize_max_tokens: 400,
  query_messages: 2,
};

/** `memory_policy` with per-field fallback (documents written before migration 0012, garbage values). */
export function memoryPolicyFor(raw: Partial<MemoryPolicy> | undefined): MemoryPolicy {
  const d = DEFAULT_MEMORY_POLICY;
  const posInt = (v: unknown, fallback: number) => (isPosNumber(v) ? Math.floor(v) : fallback);
  return {
    embedding_model: typeof raw?.embedding_model === 'string' && raw.embedding_model ? raw.embedding_model : d.embedding_model,
    embedding_dimensions: posInt(raw?.embedding_dimensions, d.embedding_dimensions),
    chunk_size: posInt(raw?.chunk_size, d.chunk_size),
    chunk_overlap_pct: isNonNegNumber(raw?.chunk_overlap_pct) && raw.chunk_overlap_pct < 50 ? Math.floor(raw.chunk_overlap_pct) : d.chunk_overlap_pct,
    max_chunks_per_user: posInt(raw?.max_chunks_per_user, d.max_chunks_per_user),
    databank_file_max_bytes: posInt(raw?.databank_file_max_bytes, d.databank_file_max_bytes),
    databank_total_bytes: { free: posInt(raw?.databank_total_bytes?.free, d.databank_total_bytes.free), pro: posInt(raw?.databank_total_bytes?.pro, d.databank_total_bytes.pro) },
    summarize_max_tokens: posInt(raw?.summarize_max_tokens, d.summarize_max_tokens),
    query_messages: posInt(raw?.query_messages, d.query_messages),
  };
}

/** Platform tag taxonomy (change `add-tag-taxonomy`): categories carry the colours, tags carry multilingual names. */
export interface TagCategory {
  key: string;
  color: string;
  color2: string;
  /** `{ <BCP 47>: name }` — at least `default_locale`. */
  names: Record<string, string>;
  sort: number;
}
export interface TaxonomyTag {
  key: string;
  category: string;
  names: Record<string, string>;
  /** Extra strings recognised on import (any language); never sent to clients. */
  aliases: string[];
  sort: number;
}
export interface TagTaxonomy {
  default_locale: string;
  categories: TagCategory[];
  tags: TaxonomyTag[];
}

/** Seed locales for platform tags (App languages). `ja` / `ko` match `ja-JP` / `ko-KR` via primary-subtag negotiation. */
export const TAXONOMY_SEED_LOCALES = ['en', 'zh-CN', 'zh-TW', 'ja', 'ko'] as const;
export type TaxonomySeedLocale = (typeof TAXONOMY_SEED_LOCALES)[number];
type SeedNames = Record<TaxonomySeedLocale, string>;

const cat = (key: string, color: string, color2: string, names: SeedNames, sort: number): TagCategory => ({ key, color, color2, names, sort });
const tag = (key: string, category: string, names: SeedNames, sort: number, aliases: string[] = []): TaxonomyTag => ({ key, category, names, aliases, sort });

/** Seed vocabulary (mirrors migrations/0013 + 0027). Operators extend it through `PUT /api/admin/config/tag_taxonomy`. */
export const DEFAULT_TAG_TAXONOMY: TagTaxonomy = {
  default_locale: 'en',
  categories: [
    // color = design swatch (foreground). color2 = same hue darkened for the chip background.
    cat('genre', '#a09cc8', '#333240', { en: 'Genre', 'zh-CN': '题材', 'zh-TW': '題材', ja: 'ジャンル', ko: '장르' }, 10),
    cat('relationship', '#bc8e8e', '#3c2d2d', { en: 'Relationship', 'zh-CN': '关系', 'zh-TW': '關係', ja: '関係', ko: '관계' }, 20),
    cat('personality', '#90b7b6', '#2e3b3a', { en: 'Personality', 'zh-CN': '性格', 'zh-TW': '性格', ja: '性格', ko: '성격' }, 30),
    cat('rating', '#ad98bc', '#37313c', { en: 'Rating', 'zh-CN': '分级', 'zh-TW': '分級', ja: 'レーティング', ko: '등급' }, 40),
    cat('source', '#9ebfaf', '#333d38', { en: 'Source', 'zh-CN': '来源', 'zh-TW': '來源', ja: '出典', ko: '출처' }, 50),
  ],
  tags: [
    tag('fantasy', 'genre', { en: 'Fantasy', 'zh-CN': '奇幻', 'zh-TW': '奇幻', ja: 'ファンタジー', ko: '판타지' }, 10),
    tag('scifi', 'genre', { en: 'Sci-Fi', 'zh-CN': '科幻', 'zh-TW': '科幻', ja: 'SF', ko: 'SF' }, 20, ['sci-fi', 'science fiction', 'sf']),
    tag('romance', 'genre', { en: 'Romance', 'zh-CN': '恋爱', 'zh-TW': '戀愛', ja: '恋愛', ko: '로맨스' }, 30, ['love']),
    tag('horror', 'genre', { en: 'Horror', 'zh-CN': '恐怖', 'zh-TW': '恐怖', ja: 'ホラー', ko: '호러' }, 40),
    tag('slice-of-life', 'genre', { en: 'Slice of Life', 'zh-CN': '日常', 'zh-TW': '日常', ja: '日常', ko: '일상' }, 50, ['slice-of-life', 'sliceoflife']),
    tag('adventure', 'genre', { en: 'Adventure', 'zh-CN': '冒险', 'zh-TW': '冒險', ja: '冒険', ko: '모험' }, 60),
    tag('mystery', 'genre', { en: 'Mystery', 'zh-CN': '悬疑', 'zh-TW': '懸疑', ja: 'ミステリー', ko: '미스터리' }, 70, ['detective']),
    tag('historical', 'genre', { en: 'Historical', 'zh-CN': '历史', 'zh-TW': '歷史', ja: '歴史', ko: '역사' }, 80),
    tag('modern', 'genre', { en: 'Modern', 'zh-CN': '现代', 'zh-TW': '現代', ja: '現代', ko: '현대' }, 90, ['contemporary']),
    tag('comedy', 'genre', { en: 'Comedy', 'zh-CN': '喜剧', 'zh-TW': '喜劇', ja: 'コメディ', ko: '코미디' }, 100, ['humor', 'humour']),
    tag('friend', 'relationship', { en: 'Friend', 'zh-CN': '朋友', 'zh-TW': '朋友', ja: '友人', ko: '친구' }, 10),
    tag('lover', 'relationship', { en: 'Lover', 'zh-CN': '恋人', 'zh-TW': '戀人', ja: '恋人', ko: '연인' }, 20, ['girlfriend', 'boyfriend', 'partner']),
    tag('family', 'relationship', { en: 'Family', 'zh-CN': '家人', 'zh-TW': '家人', ja: '家族', ko: '가족' }, 30),
    tag('rival', 'relationship', { en: 'Rival', 'zh-CN': '对手', 'zh-TW': '對手', ja: 'ライバル', ko: '라이벌' }, 40, ['enemy']),
    tag('mentor', 'relationship', { en: 'Mentor', 'zh-CN': '导师', 'zh-TW': '導師', ja: '導師', ko: '멘토' }, 50, ['teacher']),
    tag('stranger', 'relationship', { en: 'Stranger', 'zh-CN': '陌生人', 'zh-TW': '陌生人', ja: '他人', ko: '타인' }, 60),
    tag('gentle', 'personality', { en: 'Gentle', 'zh-CN': '温柔', 'zh-TW': '溫柔', ja: '優しい', ko: '온화' }, 10, ['kind', 'caring']),
    tag('tsundere', 'personality', { en: 'Tsundere', 'zh-CN': '傲娇', 'zh-TW': '傲嬌', ja: 'ツンデレ', ko: '츤데레' }, 20),
    tag('cold', 'personality', { en: 'Cold', 'zh-CN': '冷淡', 'zh-TW': '冷淡', ja: 'クール', ko: '냉정' }, 30, ['aloof']),
    tag('cheerful', 'personality', { en: 'Cheerful', 'zh-CN': '开朗', 'zh-TW': '開朗', ja: '明るい', ko: '명랑' }, 40, ['energetic']),
    tag('mysterious', 'personality', { en: 'Mysterious', 'zh-CN': '神秘', 'zh-TW': '神秘', ja: '謎めいた', ko: '신비' }, 50),
    tag('dominant', 'personality', { en: 'Dominant', 'zh-CN': '强势', 'zh-TW': '強勢', ja: 'ドミナント', ko: '주도적' }, 60),
    tag('shy', 'personality', { en: 'Shy', 'zh-CN': '害羞', 'zh-TW': '害羞', ja: '内気', ko: '수줍음' }, 70, ['timid']),
    tag('sfw', 'rating', { en: 'SFW', 'zh-CN': '全年龄', 'zh-TW': '全年齡', ja: '全年齢', ko: '전연령' }, 10, ['all ages', 'safe']),
    tag('nsfw', 'rating', { en: 'NSFW', 'zh-CN': '成人', 'zh-TW': '成人', ja: '成人向け', ko: '성인' }, 20, ['18+', 'r18', 'adult', 'mature']),
    tag('original', 'source', { en: 'Original', 'zh-CN': '原创', 'zh-TW': '原創', ja: 'オリジナル', ko: '오리지널' }, 10, ['oc']),
    tag('fanwork', 'source', { en: 'Fan Work', 'zh-CN': '同人', 'zh-TW': '同人', ja: '二次創作', ko: '팬워크' }, 20, ['fanfic', 'fan fiction']),
    tag('game', 'source', { en: 'Game', 'zh-CN': '游戏', 'zh-TW': '遊戲', ja: 'ゲーム', ko: '게임' }, 30),
    tag('anime', 'source', { en: 'Anime', 'zh-CN': '动画', 'zh-TW': '動畫', ja: 'アニメ', ko: '애니메이션' }, 40, ['animation']),
    tag('novel', 'source', { en: 'Novel', 'zh-CN': '小说', 'zh-TW': '小說', ja: '小説', ko: '소설' }, 50, ['book']),
  ],
};

const TAG_KEY_RE = /^[a-z0-9_-]{1,64}$/;
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
const LOCALE_RE = /^[a-zA-Z]{2,3}(-[a-zA-Z0-9]{2,8})*$/;

function cleanNames(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) if (LOCALE_RE.test(k) && typeof v === 'string' && v.trim()) out[k] = v.trim();
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * `tag_taxonomy` with structural fallback: a document that is not a usable taxonomy (missing before migration 0013,
 * or corrupted) yields the compiled-in default; individually malformed entries are dropped rather than failing the
 * whole vocabulary. Full validation with error reporting is the admin PUT's zod schema.
 */
export function tagTaxonomyFor(raw: Partial<TagTaxonomy> | undefined): TagTaxonomy {
  if (!raw || typeof raw !== 'object' || !Array.isArray(raw.categories) || !Array.isArray(raw.tags) || typeof raw.default_locale !== 'string' || !LOCALE_RE.test(raw.default_locale)) return DEFAULT_TAG_TAXONOMY;
  const def = raw.default_locale;
  const categories: TagCategory[] = [];
  const catKeys = new Set<string>();
  for (const c of raw.categories as Array<Partial<TagCategory>>) {
    const names = cleanNames(c?.names);
    if (!c || typeof c.key !== 'string' || !TAG_KEY_RE.test(c.key) || catKeys.has(c.key) || !names || !(def in names)) continue;
    catKeys.add(c.key);
    categories.push({ key: c.key, color: typeof c.color === 'string' && HEX_COLOR_RE.test(c.color) ? c.color : '#3f3f46', color2: typeof c.color2 === 'string' && HEX_COLOR_RE.test(c.color2) ? c.color2 : '#18181b', names, sort: Number.isFinite(c.sort) ? Math.floor(c.sort as number) : 0 });
  }
  const tags: TaxonomyTag[] = [];
  const tagKeys = new Set<string>();
  for (const t of raw.tags as Array<Partial<TaxonomyTag>>) {
    const names = cleanNames(t?.names);
    if (!t || typeof t.key !== 'string' || !TAG_KEY_RE.test(t.key) || tagKeys.has(t.key) || typeof t.category !== 'string' || !catKeys.has(t.category) || !names || !(def in names)) continue;
    tagKeys.add(t.key);
    tags.push({ key: t.key, category: t.category, names, aliases: Array.isArray(t.aliases) ? t.aliases.filter((a): a is string => typeof a === 'string' && !!a.trim()) : [], sort: Number.isFinite(t.sort) ? Math.floor(t.sort as number) : 0 });
  }
  if (categories.length === 0) return DEFAULT_TAG_TAXONOMY;
  return { default_locale: def, categories, tags };
}

/**
 * Config keys stored in `app_config` / KV but *not* embedded in the public `GET /api/config` document.
 * Rule: the public document only carries small, locale-independent, per-user-independent startup keys; any key with
 * its own endpoint (`models` → `GET /api/models`, `tag_taxonomy` → `GET /api/tags/taxonomy`) is served there instead
 * (computed fields such as `available_for_me` / `credits_per_1k` / localized names only exist on those endpoints).
 * Admins read the raw value via `GET /api/admin/config/{key}`.
 */
export const SERVED_SEPARATELY_CONFIG_KEYS: AppConfigKey[] = ['models', 'tag_taxonomy', 'custom_model_catalog'];

/** In-app release notes per platform for the recommended-update screen (change 11); "force" is derived from `client_min_version`. */
export interface ClientLatestVersionEntry {
  version: string;
  notes: Array<{ type: 'feature' | 'improvement' | 'fix'; title: string; desc?: string }>;
  released_at?: string;
}
export type ClientLatestVersion = Record<'ios' | 'android' | 'web', ClientLatestVersionEntry>;
export const DEFAULT_CLIENT_LATEST_VERSION: ClientLatestVersion = {
  ios: { version: '0.0.0', notes: [] },
  android: { version: '0.0.0', notes: [] },
  web: { version: '0.0.0', notes: [] },
};

/** Bundled seed catalog that admin `library_defaults` may reference (keep in lockstep with `workers/core` SEED). */
export const LIBRARY_DEFAULTS_CATALOG = {
  openai_preset_names: ['Default'] as const,
  world_book_names: ['Eldoria'] as const,
};

export interface LibraryDefaultsPersona {
  name: string;
  description: Record<string, unknown>;
}

/** New-user library overlay. `persona: null` = do not insert a default persona. */
export interface LibraryDefaults {
  persona: LibraryDefaultsPersona | null;
  preset_name: string;
  global_select: string[];
}

export const DEFAULT_LIBRARY_DEFAULTS: LibraryDefaults = {
  persona: {
    name: 'Traveler',
    description: {
      description: '',
      position: 0,
      depth: 2,
      role: 0,
      lorebook: '',
      title: '',
      pockettavern: { avatar_source: 'letter', avatar_color: '#c9a66b', avatar_text: 'T' },
    },
  },
  preset_name: 'Default',
  global_select: [],
};

export function libraryDefaultsFor(raw: Partial<LibraryDefaults> | undefined): LibraryDefaults {
  const d = DEFAULT_LIBRARY_DEFAULTS;
  if (!raw || typeof raw !== 'object') return d;
  let persona: LibraryDefaultsPersona | null = d.persona;
  if (raw.persona === null) persona = null;
  else if (raw.persona && typeof raw.persona === 'object' && typeof raw.persona.name === 'string' && raw.persona.name.trim()) {
    const desc = raw.persona.description && typeof raw.persona.description === 'object' && !Array.isArray(raw.persona.description)
      ? raw.persona.description
      : {};
    persona = { name: raw.persona.name.trim(), description: desc };
  }
  const preset = typeof raw.preset_name === 'string' && raw.preset_name.trim() ? raw.preset_name.trim() : d.preset_name;
  const select = Array.isArray(raw.global_select) && raw.global_select.every((n) => typeof n === 'string')
    ? raw.global_select.map((n) => n.trim()).filter(Boolean)
    : d.global_select;
  return { persona, preset_name: preset, global_select: select };
}

export type CustomModelProviderId = 'openai' | 'deepseek' | 'kimi' | 'qwen' | 'google-ai-studio';
export type CustomModelAdapter = 'openai-compatible' | 'google-ai-studio';

export interface CustomModelCatalogEntry {
  id: string;
  display_name: string;
}

export interface CustomModelCatalogProvider {
  id: CustomModelProviderId;
  display_name: string;
  adapter: CustomModelAdapter;
  base_url: string;
  models: CustomModelCatalogEntry[];
}

export interface CustomModelCatalog {
  providers: CustomModelCatalogProvider[];
}

/** Custom-model catalog. Add/remove provider or model ids only with a D1 migration. */
export const DEFAULT_CUSTOM_MODEL_CATALOG: CustomModelCatalog = {
  providers: [
    {
      id: 'openai',
      display_name: 'OpenAI',
      adapter: 'openai-compatible',
      base_url: 'https://api.openai.com/v1',
      models: [
        { id: 'gpt-4o', display_name: 'GPT-4o' },
        { id: 'gpt-4o-mini', display_name: 'GPT-4o mini' },
        { id: 'gpt-4.1', display_name: 'GPT-4.1' },
      ],
    },
    {
      id: 'deepseek',
      display_name: 'DeepSeek',
      adapter: 'openai-compatible',
      base_url: 'https://api.deepseek.com',
      models: [
        { id: 'deepseek-chat', display_name: 'DeepSeek Chat' },
        { id: 'deepseek-reasoner', display_name: 'DeepSeek Reasoner' },
      ],
    },
    {
      id: 'kimi',
      display_name: 'Kimi',
      adapter: 'openai-compatible',
      base_url: 'https://api.moonshot.ai/v1',
      models: [
        { id: 'kimi-k2', display_name: 'Kimi K2' },
        { id: 'moonshot-v1-128k', display_name: 'Moonshot v1 128k' },
      ],
    },
    {
      id: 'qwen',
      display_name: 'Qwen',
      adapter: 'openai-compatible',
      base_url: 'https://dashscope-intl.aliyuncs.com/compatible-mode/v1',
      models: [
        { id: 'qwen-plus', display_name: 'Qwen Plus' },
        { id: 'qwen-max', display_name: 'Qwen Max' },
        { id: 'qwen-turbo', display_name: 'Qwen Turbo' },
      ],
    },
    {
      id: 'google-ai-studio',
      display_name: 'Google',
      adapter: 'google-ai-studio',
      base_url: 'https://generativelanguage.googleapis.com',
      models: [
        { id: 'gemini-2.5-flash', display_name: 'Gemini 2.5 Flash' },
        { id: 'gemini-2.5-pro', display_name: 'Gemini 2.5 Pro' },
      ],
    },
  ],
};

const CATALOG_PROVIDER_IDS = new Set<string>(['openai', 'deepseek', 'kimi', 'qwen', 'google-ai-studio']);
const CATALOG_ADAPTERS = new Set<string>(['openai-compatible', 'google-ai-studio']);

/** Literal / hostname policy aligned with ai-proxy BYOK SSRF (https, no loopback / private / own domains). */
export function catalogBaseUrlOk(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:') return false;
  if (url.username || url.password) return false;
  const host = url.hostname.toLowerCase();
  if (!host.includes('.')) return false;
  if (host === 'localhost' || host === 'metadata' || host === 'metadata.google.internal' || host === 'instance-data') return false;
  if (host.endsWith('.localhost') || host.endsWith('.local') || host.endsWith('.internal') || host.endsWith('.localdomain') || host.endsWith('.home.arpa')) return false;
  if (host.endsWith('.workers.dev') || host.endsWith('.pockettavern.dev') || host.endsWith('.pockettavern.app')) return false;
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (m) {
    const [a, b] = [Number(m[1]), Number(m[2])];
    if (a === 10 || a === 127 || a === 0) return false;
    if (a === 172 && b >= 16 && b <= 31) return false;
    if (a === 192 && b === 168) return false;
    if (a === 169 && b === 254) return false;
    if (a === 100 && b >= 64 && b <= 127) return false;
    if (a >= 224) return false;
  }
  if (host.includes(':')) return false;
  return true;
}

export function customModelCatalogFor(raw: Partial<CustomModelCatalog> | undefined): CustomModelCatalog {
  const d = DEFAULT_CUSTOM_MODEL_CATALOG;
  if (!raw || typeof raw !== 'object' || !Array.isArray(raw.providers) || raw.providers.length === 0) return d;
  const seenProviders = new Set<string>();
  const providers: CustomModelCatalogProvider[] = [];
  for (const p of raw.providers) {
    if (!p || typeof p !== 'object') continue;
    if (typeof p.id !== 'string' || !CATALOG_PROVIDER_IDS.has(p.id) || seenProviders.has(p.id)) continue;
    if (typeof p.display_name !== 'string' || !p.display_name.trim()) continue;
    if (typeof p.adapter !== 'string' || !CATALOG_ADAPTERS.has(p.adapter)) continue;
    if (typeof p.base_url !== 'string' || !catalogBaseUrlOk(p.base_url)) continue;
    if (!Array.isArray(p.models) || p.models.length === 0) continue;
    const seenModels = new Set<string>();
    const models: CustomModelCatalogEntry[] = [];
    for (const m of p.models) {
      if (!m || typeof m.id !== 'string' || !m.id.trim() || seenModels.has(m.id)) continue;
      if (typeof m.display_name !== 'string' || !m.display_name.trim()) continue;
      seenModels.add(m.id);
      models.push({ id: m.id.trim(), display_name: m.display_name.trim() });
    }
    if (models.length === 0) continue;
    seenProviders.add(p.id);
    providers.push({
      id: p.id as CustomModelProviderId,
      display_name: p.display_name.trim(),
      adapter: p.adapter as CustomModelAdapter,
      base_url: p.base_url,
      models,
    });
  }
  return providers.length ? { providers } : d;
}

export function findCatalogModel(
  catalog: CustomModelCatalog,
  provider: string,
  model: string,
): { provider: CustomModelCatalogProvider; model: CustomModelCatalogEntry } | undefined {
  const p = catalog.providers.find((x) => x.id === provider);
  if (!p) return undefined;
  const m = p.models.find((x) => x.id === model);
  return m ? { provider: p, model: m } : undefined;
}

/** Catalog model ids are unique across providers in the seeded list; still scan all in case of overlap. */
export function findCatalogModelsById(
  catalog: CustomModelCatalog,
  model: string,
): Array<{ provider: CustomModelCatalogProvider; model: CustomModelCatalogEntry }> {
  const hits: Array<{ provider: CustomModelCatalogProvider; model: CustomModelCatalogEntry }> = [];
  for (const p of catalog.providers) {
    const m = p.models.find((x) => x.id === model);
    if (m) hits.push({ provider: p, model: m });
  }
  return hits;
}

export interface AppConfig {
  tier_features: Record<VipTier, string[]>;
  rate_limits: Record<VipTier, RateLimitBuckets>;
  client_min_version: { ios: string; android: string; web: string };
  feature_flags: Record<string, boolean>;
  /** Per-tier UserVault storage quota in bytes. */
  vault_quota_bytes: Record<VipTier, number>;
  media_limits: MediaLimits;
  /** Execution point 2 limits (change 5). */
  ai_limits: AiLimits;
  /** Default official model id per tier (registry id). */
  official_defaults: Record<VipTier, string>;
  /** Official model registry. */
  models: ModelEntry[];
  /** Plan entitlements: per-tier count caps (change 9). */
  plan_limits: PlanLimits;
  /** Account lifecycle knobs (change 10). */
  account_policy: AccountPolicy;
  /** Credits ("钻石") policy (change 11). */
  credits_policy: CreditsPolicy;
  /** Latest client build + in-app release notes per platform (change 11). */
  client_latest_version: ClientLatestVersion;
  /** IAP catalog + lifecycle knobs (change 12). */
  billing_products: BillingProducts;
  /** Memory modules (change 15): embeddings, chunking, Data Bank quotas, summarize cap. */
  memory_policy: MemoryPolicy;
  /** Platform tag taxonomy (add-tag-taxonomy); served by `GET /api/tags/taxonomy`, not embedded in `GET /api/config`. */
  tag_taxonomy: TagTaxonomy;
  /**
   * New-vault overlay (add-library-defaults). Applied once when `seeded_at` is still empty.
   * Not a live compile fallback — Chat Settings read the user's own library / settings.
   */
  library_defaults: LibraryDefaults;
  /** User custom-model wizard catalog (add-user-custom-models). Served by GET /api/custom-models/catalog, not GET /api/config. */
  custom_model_catalog: CustomModelCatalog;
}

export type AppConfigKey = keyof AppConfig;

export const DEFAULT_CONFIG: AppConfig = {
  tier_features: {
    // v2 prototype: preset editing is for everyone; only `local_backend` (product "自有 API") and AI character generation stay pro-only
    free: ['basic_chat', 'custom_presets'],
    // custom_presets: create / edit / delete / duplicate presets; local_backend: `credential_source = local` profiles (product "BYOK");
    // ai_character_gen (change 13): one-line character generation, editor auto-fill, per-field regeneration
    pro: ['basic_chat', 'advanced_editor', 'byok', 'raw_proxy', 'custom_presets', 'local_backend', 'ai_character_gen'],
  },
  rate_limits: {
    free: { read: 120, write: 30 },
    pro: { read: 600, write: 120 },
  },
  client_min_version: { ios: '0.0.0', android: '0.0.0', web: '0.0.0' },
  feature_flags: {},
  vault_quota_bytes: {
    free: 200 * 1024 * 1024,
    pro: 2 * 1024 * 1024 * 1024,
  },
  media_limits: {
    image_max_bytes: 10 * 1024 * 1024,
    audio_max_bytes: 20 * 1024 * 1024,
    allowed_image_types: ['image/png', 'image/jpeg', 'image/webp', 'image/gif'],
    allowed_audio_types: ['audio/mpeg', 'audio/wav', 'audio/ogg'],
  },
  ai_limits: {
    first_byte_ms: 45_000,
    total_ms: 150_000,
    byok_max_secrets: 20,
    max_tokens_ceiling: { free: 2048, pro: 8192 },
    sampling_ranges: { temperature: [0, 2], top_p: [0, 1], frequency_penalty: [-2, 2], presence_penalty: [-2, 2] },
    group_max_turns: { free: 2, pro: 5 },
    max_tool_depth: { free: 3, pro: 5 },
    auto_continue_max: { free: 2, pro: 5 },
    character_gen: DEFAULT_CHARACTER_GEN_LIMITS,
  },
  official_defaults: { free: 'gemini-2.5-flash', pro: 'gemini-2.5-flash' },
  /**
   * Fail-safe registry (mirrors migrations/0003 seeds + 0007 + 0022 + 0023 + 0024 + 0025 + 0030 + 0031). Routes verified
   * against the AI Gateway / Workers AI catalog. `provider` is the model family for grouping, not the host.
   * `tier_min` is `free` everywhere since change 9 (D12: official models are metered by credits, not tier-gated);
   * admins may set `pro` on a model as an emergency lock. Gemini Flash is the both-tier default. Enabled chat models
   * also include Gemini Pro, Workers AI DeepSeek V4 Flash / Pro (`@cf/deepseek-ai/deepseek-v4-flash-0731`,
   * `@cf/deepseek-ai/deepseek-v4-pro-0813`), Llama 4 Scout, and Llama 3.3 70B.
   * Qwen3 30B-A3B, Qwen3.8 27B, and Gemma 4 26B-A4B are disabled (too small for chat prompts). GLM-4.7 Flash /
   * Claude / GPT / Grok remain `enabled: false`. Kimi K2.6 / GLM-5.x (50 rpm Paid) stay out of the main pool.
   */
  models: [
    { id: 'gemini-2.5-flash', provider: 'google-ai-studio', route: 'google/gemini-2.5-flash', display_name: 'Gemini 2.5 Flash', context_length: 1_048_576, max_output: 65_536, pricing: { input: 0.3, output: 2.5 }, capabilities: { reasoning: true, tools: true, vision: true }, content_profile: 'sfw', channel: 'official', tier_min: 'free', priority: 10, enabled: true },
    { id: 'deepseek-v4-flash', provider: 'deepseek', route: '@cf/deepseek-ai/deepseek-v4-flash-0731', display_name: 'DeepSeek V4 Flash', context_length: 1_048_576, max_output: 32_768, pricing: { input: 0.3, output: 1.2 }, capabilities: { reasoning: true, tools: true }, content_profile: 'any', channel: 'official', tier_min: 'free', priority: 18, enabled: true },
    { id: 'deepseek-v4-pro', provider: 'deepseek', route: '@cf/deepseek-ai/deepseek-v4-pro-0813', display_name: 'DeepSeek V4 Pro', context_length: 1_048_576, max_output: 32_768, pricing: { input: 1.32, output: 3.96, cached_input: 0.044 }, capabilities: { reasoning: true, tools: true }, content_profile: 'any', channel: 'official', tier_min: 'free', priority: 12, enabled: true },
    { id: 'gemini-2.5-pro', provider: 'google-ai-studio', route: 'google/gemini-2.5-pro', display_name: 'Gemini 2.5 Pro', context_length: 1_048_576, max_output: 65_536, pricing: { input: 1.25, output: 10 }, capabilities: { reasoning: true, tools: true, vision: true }, content_profile: 'sfw', channel: 'official', tier_min: 'free', priority: 20, enabled: true },
    { id: 'claude-sonnet-4-5', provider: 'anthropic', route: 'anthropic/claude-sonnet-4-5', display_name: 'Claude Sonnet 4.5', context_length: 200_000, max_output: 64_000, pricing: { input: 3, output: 15 }, capabilities: { reasoning: true, tools: true, vision: true }, content_profile: 'sfw', channel: 'official', tier_min: 'free', priority: 30, enabled: false },
    { id: 'gpt-5.5', provider: 'openai', route: 'openai/gpt-5.5', display_name: 'GPT-5.5', context_length: 400_000, max_output: 128_000, pricing: { input: 1.25, output: 10 }, capabilities: { reasoning: true, tools: true, vision: true }, content_profile: 'sfw', channel: 'official', tier_min: 'free', priority: 40, enabled: false },
    { id: 'grok-4', provider: 'x-ai', route: 'x-ai/grok-4', display_name: 'Grok 4', context_length: 256_000, max_output: 32_768, pricing: { input: 3, output: 15 }, capabilities: { reasoning: true, tools: true }, content_profile: 'any', channel: 'official', tier_min: 'free', priority: 50, enabled: false },
    { id: 'glm-4.7-flash', provider: 'glm', route: '@cf/zai-org/glm-4.7-flash', display_name: 'GLM-4.7 Flash', context_length: 131_072, max_output: 32_768, pricing: { input: 0.0605, output: 0.4 }, capabilities: { reasoning: true, tools: true }, content_profile: 'any', channel: 'official', tier_min: 'free', priority: 15, enabled: false },
    { id: 'qwen3-30b', provider: 'qwen', route: '@cf/qwen/qwen3-30b-a3b-fp8', display_name: 'Qwen3 30B', context_length: 32_768, max_output: 8_192, pricing: { input: 0.0509, output: 0.335 }, capabilities: { reasoning: true, tools: true }, content_profile: 'any', channel: 'official', tier_min: 'free', priority: 35, enabled: false },
    { id: 'gemma-4-26b', provider: 'gemma', route: '@cf/google/gemma-4-26b-a4b-it', display_name: 'Gemma 4 26B', context_length: 256_000, max_output: 32_768, pricing: { input: 0.1, output: 0.3 }, capabilities: { reasoning: true, tools: true, vision: true }, content_profile: 'sfw', channel: 'official', tier_min: 'free', priority: 30, enabled: false },
    { id: 'llama-4-scout', provider: 'llama', route: '@cf/meta/llama-4-scout-17b-16e-instruct', display_name: 'Llama 4 Scout', context_length: 131_000, max_output: 8_192, pricing: { input: 0.27, output: 0.85 }, capabilities: { tools: true, vision: true }, content_profile: 'sfw', channel: 'official', tier_min: 'free', priority: 25, enabled: true },
    { id: 'llama-3.3-70b', provider: 'llama', route: '@cf/meta/llama-3.3-70b-instruct-fp8-fast', display_name: 'Llama 3.3 70B', context_length: 24_000, max_output: 8_192, pricing: { input: 0.293, output: 2.253 }, capabilities: { tools: true }, content_profile: 'any', channel: 'official', tier_min: 'free', priority: 45, enabled: true },
    { id: 'qwen3.8-27b', provider: 'qwen', route: '@cf/qwen/qwen3.8-27b', display_name: 'Qwen3.8 27B', context_length: 262_144, max_output: 32_768, pricing: { input: 0.45, output: 3.2, cached_input: 0.05 }, capabilities: { reasoning: true, tools: true, vision: true }, content_profile: 'any', channel: 'official', tier_min: 'free', priority: 55, enabled: false },
  ],
  plan_limits: DEFAULT_PLAN_LIMITS,
  account_policy: DEFAULT_ACCOUNT_POLICY,
  credits_policy: DEFAULT_CREDITS_POLICY,
  client_latest_version: DEFAULT_CLIENT_LATEST_VERSION,
  billing_products: DEFAULT_BILLING_PRODUCTS,
  memory_policy: DEFAULT_MEMORY_POLICY,
  tag_taxonomy: DEFAULT_TAG_TAXONOMY,
  library_defaults: DEFAULT_LIBRARY_DEFAULTS,
  custom_model_catalog: DEFAULT_CUSTOM_MODEL_CATALOG,
};

export const APP_CONFIG_KEYS: AppConfigKey[] = ['tier_features', 'rate_limits', 'client_min_version', 'feature_flags', 'vault_quota_bytes', 'media_limits', 'ai_limits', 'official_defaults', 'models', 'plan_limits', 'account_policy', 'credits_policy', 'client_latest_version', 'billing_products', 'memory_policy', 'tag_taxonomy', 'library_defaults', 'custom_model_catalog'];

/** Hard bounds for admin PUT `/api/admin/config/{key}` (physical / runtime ceilings, not product recommendations). */
export const CONFIG_BOUNDS = {
  first_byte_ms: { min: 1_000, max: 120_000 },
  total_ms: { min: 1_000, max: 180_000 },
  max_tokens_ceiling: { min: 256, max: 32_768 },
  rate_limits: { min: 1, max: 10_000 },
  vault_quota_bytes: { min: 10 * 1024 * 1024, max: 50 * 1024 * 1024 * 1024 },
  media_bytes: { min: 64 * 1024, max: 100 * 1024 * 1024 },
  byok_max_secrets: { min: 1, max: 100 },
  ai_loop: { min: 1, max: 20 },
  max_concurrent_generations: { min: 1, max: 20 },
  character_gen_max_tokens: { min: 1_024, max: 32_768 },
  account_policy: {
    deletion_grace_days: { min: 1, max: 90 },
    otp_ttl_seconds: { min: 60, max: 3_600 },
    otp_max_attempts: { min: 1, max: 10 },
    otp_resend_cooldown_seconds: { min: 10, max: 600 },
    otp_hourly_limit: { min: 1, max: 50 },
    trash_retention_days: { min: 7, max: 90 },
  },
} as const;

const TIER_RANK: Record<VipTier, number> = { free: 0, pro: 1 };
export const tierAtLeast = (tier: string, min: VipTier): boolean => (TIER_RANK[tier as VipTier] ?? 0) >= TIER_RANK[min];

/**
 * Fallback chain for unspecified-model official requests (change 9 rule): the tier default first, then every enabled
 * official model the tier may use whose `pricing.output` is not higher than the default's, by priority — a failover
 * never upgrades the user to a pricier model. When the default is missing from the registry the chain is the tier's
 * usable models by priority.
 */
export function officialChainFor(config: Pick<AppConfig, 'models' | 'official_defaults'>, tier: string): ModelEntry[] {
  const usable = config.models.filter((m) => m.enabled && m.channel === 'official' && tierAtLeast(tier, m.tier_min)).sort((a, b) => a.priority - b.priority);
  const defId = config.official_defaults[tier as VipTier] ?? config.official_defaults.free;
  const def = usable.find((m) => m.id === defId);
  if (!def) return usable;
  return [def, ...usable.filter((m) => m.id !== def.id && m.pricing.output <= def.pricing.output)];
}

export function findModel(config: Pick<AppConfig, 'models'>, id: string): ModelEntry | undefined {
  return config.models.find((m) => m.id === id || m.route === id);
}

export interface ConfigDoc {
  version: number;
  config: AppConfig;
}

export const CONFIG_DOC_KV_KEY = 'config:doc';
